import React, { useContext, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import LoadingIcon from "../../images/Loading_icon.gif";
import "../../styles/AISearchModal.css";
import { UrlContext } from "../../contexts/UrlContext";
import Swal from "sweetalert2";

export default function AISearchModal({
  isOpen,
  onClose,
  results,
  isLoading,
  tableData,
  setDoRefresh,
  projectId,
  setAiSearchResults,
}) {
  const { API_URL } = useContext(UrlContext);
  const [localAddedExperts, setLocalAddedExperts] = useState({});
  const [loadingAction, setLoadingAction] = useState({});
  const [summary, setSummary] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isSearchTriggering, setIsSearchTriggering] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [hasSearchedEmpty, setHasSearchedEmpty] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [expandedExpertId, setExpandedExpertId] = useState(null);

  // Chip inputs
  const [newMustHave, setNewMustHave] = useState("");
  const [newKeyword, setNewKeyword] = useState("");
  const [newNiceToHave, setNewNiceToHave] = useState("");
  const [newLanguage, setNewLanguage] = useState("");

  // Consulting firms
  const [availableConsultingFirms, setAvailableConsultingFirms] = useState([]);
  const [newFirmName, setNewFirmName] = useState("");
  const [isAddingFirm, setIsAddingFirm] = useState(false);
  const [showFirmPicker, setShowFirmPicker] = useState(false);

  // Conversational AI Filter Chat
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [visibleExperts, setVisibleExperts] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [showChatPanel, setShowChatPanel] = useState(true);

  const autoSearchedProjectRef = useRef(null);

  useEffect(() => {
    setVisibleExperts(results || []);
    setChatHistory([]);
  }, [results]);

  useEffect(() => {
    if (isOpen) {
      fetchConsultingFirms();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && tableData) {
      const initialMap = {};
      tableData.forEach((item) => {
        const id = item.expert?.ID || item.expert?.id || item.expert_id || item.id;
        if (id) initialMap[Number(id)] = true;
      });
      setLocalAddedExperts(initialMap);
    }
  }, [isOpen, tableData]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchSummaryAndInitialize();
    }
  }, [isOpen, projectId]);

  const ensureArray = (val) => {
    if (val === undefined || val === null) return [];
    if (Array.isArray(val)) return val.map((v) => String(v).trim()).filter(Boolean);
    if (typeof val === "string") {
      return val
        .split(/\bAND\b|[,;\n]+/i)
        .map((i) => i.trim())
        .filter(Boolean);
    }
    return [String(val).trim()].filter(Boolean);
  };

  const fetchConsultingFirms = async () => {
    try {
      const response = await fetch(`${API_URL}get-consulting-firms`);
      const data = await response.json();
      if (data.status === "success" && data.consulting_firms) {
        setAvailableConsultingFirms(data.consulting_firms);
      }
    } catch (error) {
      setAvailableConsultingFirms([
        "McKinsey & Company",
        "Boston Consulting Group (BCG)",
        "Bain & Company",
        "Roland Berger",
        "Strategy&",
        "Oliver Wyman",
        "Deloitte",
        "PwC",
        "EY",
        "KPMG",
        "Accenture",
        "Capgemini",
      ]);
    }
  };

  const fetchSummaryAndInitialize = async () => {
    setIsSummaryLoading(true);
    setHasSearchedEmpty(false);
    try {
      const response = await fetch(
        "https://ai.expertpowerhouse.com/expert-search/get-project-summary",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectID: Number(projectId) }),
        }
      );
      const data = await response.json();
      if (data.status === "success" && data.summary) {
        const proj = data.summary;
        const firms = ensureArray(proj.consulting_firms);
        const normalizedSummary = {
          project_id: Number(projectId),
          country: proj.country || "",
          type: proj.type || "consultant",
          engagement_type: proj.engagement_type || "",
          seniority_score: proj.seniority_score || null,
          confidence: proj.confidence || null,
          requires_tier1_consulting: proj.requires_tier1_consulting ?? false,
          remote: proj.remote ?? false,
          keywords: ensureArray(proj.keywords),
          languages: ensureArray(proj.languages),
          musthave: ensureArray(proj.musthave),
          nice_to_have: ensureArray(proj.nice_to_have),
          consulting_firms: firms,
          is_consulting:
            (proj.is_consulting ?? false) ||
            (proj.requires_tier1_consulting ?? false) ||
            firms.length > 0,
        };
        setSummary(normalizedSummary);
        setIsDirty(false);

        const existingMemoryResults = results && results.length > 0;
        const cachedBackendExperts = data.cached_results?.experts;

        if (!existingMemoryResults && Array.isArray(cachedBackendExperts) && cachedBackendExperts.length > 0) {
          // Load cached results from DB immediately
          setAiSearchResults((prev) => ({
            ...prev,
            [projectId]: {
              experts: cachedBackendExperts,
              filters: data.cached_results.applied_filters || normalizedSummary,
            },
          }));
        } else if (
          !existingMemoryResults &&
          (!cachedBackendExperts || cachedBackendExperts.length === 0) &&
          autoSearchedProjectRef.current !== String(projectId)
        ) {
          // Auto-run search on first open if no cached results exist yet
          autoSearchedProjectRef.current = String(projectId);
          await executeSearch(normalizedSummary, false, true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const formatSummaryForAPI = (rawSummary, forceRefresh = false) => {
    if (!rawSummary) return null;
    return {
      project_id: Number(projectId),
      country: (rawSummary.country || "").trim(),
      type: rawSummary.type || "consultant",
      remote: Boolean(rawSummary.remote),
      keywords: ensureArray(rawSummary.keywords),
      languages: ensureArray(rawSummary.languages),
      musthave: ensureArray(rawSummary.musthave),
      nice_to_have: ensureArray(rawSummary.nice_to_have),
      consulting_firms: rawSummary.is_consulting
        ? ensureArray(rawSummary.consulting_firms)
        : [],
      engagement_type: rawSummary.engagement_type || "",
      seniority_score: rawSummary.seniority_score || null,
      requires_tier1_consulting: Boolean(rawSummary.requires_tier1_consulting),
      force_refresh: Boolean(forceRefresh),
    };
  };

  const executeSearch = async (targetSummary, forceRefresh = false, silent = false) => {
    const payload = formatSummaryForAPI(targetSummary || summary, forceRefresh);
    if (!payload) return;

    setIsSearchTriggering(true);
    setHasSearchedEmpty(false);

    try {
      // Save updated summary if user edited filters
      if (isDirty) {
        await fetch("https://ai.expertpowerhouse.com/expert-search/edit-project-summary", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectID: Number(projectId), summary: payload }),
        }).catch(() => {});
      }

      const response = await fetch(
        "https://ai.expertpowerhouse.com/expert-search/search-experts",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();

      if (data.status === "error" && data.message === "No results") {
        setAiSearchResults((prev) => ({
          ...prev,
          [projectId]: { experts: [], filters: payload },
        }));
        setHasSearchedEmpty(true);
      } else if (data.status === "success" && Array.isArray(data.experts)) {
        setAiSearchResults((prev) => ({
          ...prev,
          [projectId]: {
            experts: data.experts,
            filters: data.applied_filters || payload,
          },
        }));
        setIsDirty(false);
        if (data.experts.length === 0) {
          setHasSearchedEmpty(true);
        }
      } else {
        throw new Error(data.message || "Search failed");
      }
    } catch (error) {
      console.error("Search failed:", error);
      if (!silent) {
        Swal.fire({ icon: "error", title: "Search Error", text: "Could not complete AI search." });
      }
    } finally {
      setIsSearchTriggering(false);
    }
  };

  const updateSummaryField = (field, value) => {
    setSummary((prev) => (prev ? { ...prev, [field]: value } : prev));
    setIsDirty(true);
  };

  const addChip = (field, rawValue, setter) => {
    const cleaned = (rawValue || "").trim();
    if (!cleaned || !summary) return;
    const items = cleaned
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const current = ensureArray(summary[field]);
    const next = [...current];
    items.forEach((item) => {
      if (!next.some((existing) => existing.toLowerCase() === item.toLowerCase())) {
        next.push(item);
      }
    });
    updateSummaryField(field, next);
    setter("");
  };

  const removeChip = (field, itemToRemove) => {
    if (!summary) return;
    const current = ensureArray(summary[field]);
    updateSummaryField(
      field,
      current.filter((item) => item !== itemToRemove)
    );
  };

  const toggleConsultingFirm = (firm) => {
    if (!summary) return;
    const current = ensureArray(summary.consulting_firms);
    const next = current.includes(firm)
      ? current.filter((f) => f !== firm)
      : [...current, firm];
    updateSummaryField("consulting_firms", next);
  };

  const handleAddFirm = async () => {
    if (!newFirmName.trim()) return;
    setIsAddingFirm(true);
    try {
      const response = await fetch(`${API_URL}add-consulting-firm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newFirmName.trim() }),
      });
      const data = await response.json();
      if (data.status === "success") {
        const firm = data.firm || newFirmName.trim();
        setAvailableConsultingFirms((prev) =>
          prev.includes(firm) ? prev : [...prev, firm]
        );
        toggleConsultingFirm(firm);
        setNewFirmName("");
      }
    } catch (error) {
      console.error("Failed to add consulting firm:", error);
    } finally {
      setIsAddingFirm(false);
    }
  };

  const prepareExpertsForChat = (allExperts) => {
    return (allExperts || []).map((exp) => ({
      id: exp.id,
      name: exp.name,
      position: exp.position,
      country: exp.country,
      languages: exp.languages,
      skills: exp.skills,
      summary: exp.summary || exp.synthesis,
      experience: exp.experience,
    }));
  };

  const handleSendChatMessage = async (e, presetMessage = null) => {
    if (e) e.preventDefault();
    const userMsg = (presetMessage !== null ? presetMessage : chatInput).trim();
    if (!userMsg || isChatLoading) return;

    if (presetMessage === null) setChatInput("");
    const nextHistory = [...chatHistory, { role: "user", content: userMsg }];
    setChatHistory(nextHistory);
    setIsChatLoading(true);

    try {
      const response = await fetch(
        "https://ai.expertpowerhouse.com/expert-search/chat-filter",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: userMsg,
            experts: prepareExpertsForChat(visibleExperts.length > 0 ? visibleExperts : results),
            history: chatHistory,
          }),
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setChatHistory((prev) => [...prev, { role: "ai", content: data.message }]);
        const newOrderIds = data.expert_ids || [];
        const sourcePool = results || [];
        const filteredAndOrderedExperts = newOrderIds
          .map((id) => sourcePool.find((exp) => Number(exp.id) === Number(id)))
          .filter(Boolean);
        setVisibleExperts(filteredAndOrderedExperts);
      } else {
        throw new Error(data.message || "Failed to process chat");
      }
    } catch (error) {
      console.error("Chat error:", error);
      setChatHistory((prev) => [
        ...prev,
        { role: "error", content: "Could not apply filter. Try rephrasing your criteria." },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleClearChat = () => {
    setChatHistory([]);
    setVisibleExperts(results || []);
    setChatInput("");
  };

  const getScoreDetails = (expert) => {
    let score = null;
    if (
      expert.jev_score !== undefined &&
      expert.jev_score !== null &&
      !isNaN(Number(expert.jev_score))
    ) {
      score = Number(expert.jev_score);
    } else if (
      expert.adaptation_percentage !== undefined &&
      expert.adaptation_percentage !== null &&
      !isNaN(Number(expert.adaptation_percentage))
    ) {
      score = Number(expert.adaptation_percentage) / 10;
    } else if (
      expert.score !== undefined &&
      expert.score !== null &&
      !isNaN(Number(expert.score))
    ) {
      const raw = Number(expert.score);
      score = raw <= 1 ? raw * 10 : raw;
    }

    if (score === null || isNaN(score)) {
      return { text: "N/A", bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
    }

    const clamped = Math.max(0, Math.min(10, score));
    const formatted = clamped.toFixed(1);

    if (clamped >= 7.0) {
      return {
        text: `${formatted} / 10`,
        bg: "#dcfce7",
        color: "#15803d",
        border: "#86efac",
      };
    }
    if (clamped >= 5.5) {
      return {
        text: `${formatted} / 10`,
        bg: "#e0f2fe",
        color: "#0369a1",
        border: "#7dd3fc",
      };
    }
    if (clamped >= 4.0) {
      return {
        text: `${formatted} / 10`,
        bg: "#fef3c7",
        color: "#b45309",
        border: "#fde68a",
      };
    }
    return {
      text: `${formatted} / 10`,
      bg: "#fee2e2",
      color: "#dc2626",
      border: "#fca5a5",
    };
  };

  const handleSelect = (expertId) => {
    setLoadingAction((prev) => ({ ...prev, [expertId]: true }));
    const ephToken = localStorage.getItem("eph-token");
    const token = ephToken ? JSON.parse(ephToken).value : "";
    const url = `${API_URL}select-expert/${projectId}/${expertId}`;
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": token,
      },
    })
      .then((res) => res.json())
      .then(() => {
        setLocalAddedExperts((prev) => ({ ...prev, [Number(expertId)]: true }));
        setLoadingAction((prev) => ({ ...prev, [expertId]: false }));
        if (setDoRefresh) setDoRefresh((prev) => !prev);
      })
      .catch(() => {
        setLoadingAction((prev) => ({ ...prev, [expertId]: false }));
      });
  };

  const handleUnselect = (expertId) => {
    setLoadingAction((prev) => ({ ...prev, [expertId]: true }));
    const url = `${API_URL}unselect-expert/${projectId}/${expertId}`;
    fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then(() => {
        setLocalAddedExperts((prev) => ({ ...prev, [Number(expertId)]: false }));
        setLoadingAction((prev) => ({ ...prev, [expertId]: false }));
        if (setDoRefresh) setDoRefresh((prev) => !prev);
      })
      .catch(() => {
        setLoadingAction((prev) => ({ ...prev, [expertId]: false }));
      });
  };

  if (!isOpen) return null;

  const isBusy = isLoading || isSummaryLoading || isSearchTriggering;
  const selectedInResultsCount = (visibleExperts || []).filter(
    (e) => !!localAddedExperts[Number(e.id)]
  ).length;

  return (
    <div className="project-info-popup-wrapper" style={{ zIndex: 1000 }}>
      <div className="project-info-div ai-search-modal-shell">
        {/* Header Bar */}
        <div className="ai-modal-header">
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <h4 className="mb-0" style={{ color: "#1e293b", fontWeight: 700, fontSize: "1.15rem" }}>
              <i className="fa-solid fa-wand-magic-sparkles mr-2" style={{ color: "#365561" }} />
              AI Expert Search
            </h4>
            {summary?.engagement_type && (
              <span className="ai-meta-badge">
                {summary.engagement_type.replace(/_/g, " ")}
              </span>
            )}
            {summary?.seniority_score && (
              <span className="ai-meta-badge">
                Seniority {summary.seniority_score}/10
              </span>
            )}
            {visibleExperts.length > 0 && (
              <span className="text-muted small">
                Showing <b>{visibleExperts.length}</b> matches
                {selectedInResultsCount > 0 ? ` (${selectedInResultsCount} added to project)` : ""}
              </span>
            )}
          </div>

          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={() => setShowFilters(!showFilters)}
              title="Toggle search criteria bar"
            >
              <i className={`fa-solid ${showFilters ? "fa-chevron-up" : "fa-sliders"} mr-1`} />
              {showFilters ? "Hide Filters" : "Filters"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={() => setShowChatPanel(!showChatPanel)}
              title="Toggle AI refine chat"
            >
              <i className="fa-solid fa-comments mr-1" />
              {showChatPanel ? "Hide AI Chat" : "AI Refine"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={onClose}
              title="Close"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        {/* Simple Interactive Filter Bar */}
        {showFilters && (
          <div className="ai-filter-panel">
            {isSummaryLoading && !summary ? (
              <div className="d-flex align-items-center justify-content-center py-2">
                <img src={LoadingIcon} alt="Loading..." style={{ height: "22px" }} className="mr-2" />
                <span className="small text-muted">Analyzing project requirements...</span>
              </div>
            ) : summary ? (
              <>
                {/* Row 1: Core Toggles (Type, Remote, Country, Languages, Search Action) */}
                <div className="ai-filter-top-row">
                  <div className="ai-filter-group">
                    <label className="ai-filter-label">Profile</label>
                    <select
                      className="form-control form-control-sm ai-inline-select"
                      value={summary.type || "consultant"}
                      onChange={(e) => updateSummaryField("type", e.target.value)}
                    >
                      <option value="consultant">Consultant</option>
                      <option value="expert">Expert</option>
                      <option value="interim">Interim</option>
                    </select>
                  </div>

                  <div className="ai-filter-group">
                    <label className="ai-filter-label">Work Mode</label>
                    <button
                      type="button"
                      className={`ai-toggle-pill ${summary.remote ? "active" : ""}`}
                      onClick={() => updateSummaryField("remote", !summary.remote)}
                    >
                      <i className={`fa-solid ${summary.remote ? "fa-globe" : "fa-location-dot"} mr-1`} />
                      {summary.remote ? "Remote (Global)" : "Location Specific"}
                    </button>
                  </div>

                  {!summary.remote && (
                    <div className="ai-filter-group">
                      <label className="ai-filter-label">Country</label>
                      <input
                        type="text"
                        className="form-control form-control-sm ai-inline-input"
                        placeholder="Any country"
                        value={summary.country || ""}
                        onChange={(e) => updateSummaryField("country", e.target.value)}
                      />
                    </div>
                  )}

                  <div className="ai-filter-group flex-grow-1">
                    <label className="ai-filter-label">Languages</label>
                    <div className="ai-chip-list">
                      {ensureArray(summary.languages).map((lang) => (
                        <span key={lang} className="ai-chip ai-chip-neutral">
                          {lang}
                          <i
                            className="fa-solid fa-xmark ml-1 ai-chip-remove"
                            onClick={() => removeChip("languages", lang)}
                          />
                        </span>
                      ))}
                      <input
                        type="text"
                        className="ai-chip-input"
                        placeholder="+ Language (Enter)"
                        value={newLanguage}
                        onChange={(e) => setNewLanguage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addChip("languages", newLanguage, setNewLanguage);
                          }
                        }}
                        onBlur={() => addChip("languages", newLanguage, setNewLanguage)}
                      />
                    </div>
                  </div>

                  <div className="ai-filter-group">
                    <button
                      type="button"
                      className={`ai-toggle-pill ${summary.is_consulting ? "active-consulting" : ""}`}
                      onClick={() => {
                        const nextVal = !summary.is_consulting;
                        updateSummaryField("is_consulting", nextVal);
                        setShowFirmPicker(nextVal);
                      }}
                    >
                      <i className="fa-solid fa-briefcase mr-1" />
                      Consulting Firms
                      {summary.is_consulting && ensureArray(summary.consulting_firms).length > 0
                        ? ` (${ensureArray(summary.consulting_firms).length})`
                        : ""}
                    </button>
                  </div>

                  <div className="ai-filter-actions">
                    <button
                      type="button"
                      className={`btn btn-sm ${isDirty ? "btn-primary" : "base-btn"} px-3`}
                      style={{ fontWeight: 600, height: "32px", whiteSpace: "nowrap" }}
                      onClick={() => executeSearch(summary, true, false)}
                      disabled={isBusy}
                    >
                      {isSearchTriggering ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-1" /> Searching...
                        </>
                      ) : isDirty ? (
                        <>
                          <i className="fa-solid fa-magnifying-glass mr-1" /> Update Search
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-rotate mr-1" /> Refresh Search
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Row 2: Must-Have Chips & Domain Keywords Chips */}
                <div className="ai-filter-chips-grid mt-2">
                  <div className="ai-chip-section">
                    <span className="ai-chip-section-title text-success">
                      <i className="fa-solid fa-circle-check mr-1" />
                      Must-Have Skills / Tools:
                    </span>
                    <div className="ai-chip-list">
                      {ensureArray(summary.musthave).map((mh) => (
                        <span key={mh} className="ai-chip ai-chip-must">
                          {mh}
                          <i
                            className="fa-solid fa-xmark ml-1 ai-chip-remove"
                            onClick={() => removeChip("musthave", mh)}
                          />
                        </span>
                      ))}
                      <input
                        type="text"
                        className="ai-chip-input"
                        placeholder="+ Add must-have (Enter)"
                        value={newMustHave}
                        onChange={(e) => setNewMustHave(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addChip("musthave", newMustHave, setNewMustHave);
                          }
                        }}
                        onBlur={() => addChip("musthave", newMustHave, setNewMustHave)}
                      />
                    </div>
                  </div>

                  <div className="ai-chip-section">
                    <span className="ai-chip-section-title" style={{ color: "#365561" }}>
                      <i className="fa-solid fa-magnifying-glass mr-1" />
                      Domain Keywords:
                    </span>
                    <div className="ai-chip-list">
                      {ensureArray(summary.keywords).map((kw) => (
                        <span key={kw} className="ai-chip ai-chip-keyword">
                          {kw}
                          <i
                            className="fa-solid fa-xmark ml-1 ai-chip-remove"
                            onClick={() => removeChip("keywords", kw)}
                          />
                        </span>
                      ))}
                      <input
                        type="text"
                        className="ai-chip-input"
                        placeholder="+ Add keyword (Enter)"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addChip("keywords", newKeyword, setNewKeyword);
                          }
                        }}
                        onBlur={() => addChip("keywords", newKeyword, setNewKeyword)}
                      />
                    </div>
                  </div>

                  <div className="ai-chip-section">
                    <span className="ai-chip-section-title text-muted">
                      <i className="fa-solid fa-star mr-1" />
                      Nice-to-Have:
                    </span>
                    <div className="ai-chip-list">
                      {ensureArray(summary.nice_to_have).map((nth) => (
                        <span key={nth} className="ai-chip ai-chip-nice">
                          {nth}
                          <i
                            className="fa-solid fa-xmark ml-1 ai-chip-remove"
                            onClick={() => removeChip("nice_to_have", nth)}
                          />
                        </span>
                      ))}
                      <input
                        type="text"
                        className="ai-chip-input"
                        placeholder="+ Bonus skill (Enter)"
                        value={newNiceToHave}
                        onChange={(e) => setNewNiceToHave(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addChip("nice_to_have", newNiceToHave, setNewNiceToHave);
                          }
                        }}
                        onBlur={() => addChip("nice_to_have", newNiceToHave, setNewNiceToHave)}
                      />
                    </div>
                  </div>
                </div>

                {/* Optional Consulting Firms Drawer */}
                {summary.is_consulting && (
                  <div className="ai-consulting-drawer mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small font-weight-bold text-muted">
                        Target Consulting Firms:
                      </span>
                      <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-secondary"
                          onClick={() =>
                            updateSummaryField("consulting_firms", [...availableConsultingFirms])
                          }
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-secondary"
                          onClick={() => updateSummaryField("consulting_firms", [])}
                        >
                          Clear
                        </button>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          style={{ width: "140px", height: "24px", fontSize: "11px" }}
                          placeholder="Add firm..."
                          value={newFirmName}
                          onChange={(e) => setNewFirmName(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-primary"
                          onClick={handleAddFirm}
                          disabled={isAddingFirm || !newFirmName.trim()}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                      {availableConsultingFirms.map((firm) => {
                        const isSelected = ensureArray(summary.consulting_firms).includes(firm);
                        return (
                          <span
                            key={firm}
                            onClick={() => toggleConsultingFirm(firm)}
                            className={`ai-firm-pill ${isSelected ? "selected" : ""}`}
                          >
                            {firm}
                            {isSelected && <i className="fa-solid fa-check ml-1" />}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted small text-center py-1">
                Could not load project summary.
              </div>
            )}
          </div>
        )}

        {/* Main Content: Results Table + Optional AI Refine Sidebar */}
        <div className="d-flex flex-grow-1" style={{ overflow: "hidden", minHeight: "250px" }}>
          <div className="results-container flex-grow-1" style={{ overflowY: "auto", paddingRight: "10px" }}>
            {isBusy && visibleExperts.length === 0 ? (
              <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5">
                <img src={LoadingIcon} alt="Loading..." style={{ height: "45px" }} />
                <p className="mt-3 mb-1 font-weight-bold" style={{ color: "#365561" }}>
                  Finding and ranking the best candidates...
                </p>
                <p className="text-muted small">
                  Evaluating semantic similarity, must-have skills, and TypeSafe Jev calibration
                </p>
              </div>
            ) : visibleExperts.length > 0 ? (
              <table className="table table-hover ai-results-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "4%" }}>#</th>
                    <th style={{ width: "31%" }}>Candidate</th>
                    <th style={{ width: "14%" }}>AI Fit Score</th>
                    <th style={{ width: "38%" }}>Why This Candidate Matches</th>
                    <th style={{ width: "13%", textAlign: "right" }}>Project</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleExperts.map((expert, index) => {
                    const isAdded = !!localAddedExperts[Number(expert.id)];
                    const scoreDetails = getScoreDetails(expert);
                    const isExpanded = expandedExpertId === expert.id;
                    const totalMh = Number(expert.total_mh || 0);
                    const matchedMh = Number(expert.mh_matches || 0);

                    return (
                      <React.Fragment key={expert.id || index}>
                        <tr className={isAdded ? "ai-row-selected" : ""}>
                          <td className="align-middle text-muted font-weight-bold">
                            {index + 1}
                          </td>
                          <td className="align-middle">
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: "6px" }}>
                              <Link
                                to={`/app/ExpertDetails/${expert.id}`}
                                target="_blank"
                                className="expert-name-link"
                              >
                                {expert.name}
                              </Link>
                              {expert.is_vip && (
                                <span className="ai-badge-vip" title="VIP Priority Expert">
                                  ★ VIP
                                </span>
                              )}
                              {expert.verified === false && (
                                <span className="ai-badge-unverified" title="Unverified Profile">
                                  Unverified
                                </span>
                              )}
                            </div>

                            {expert.position && (
                              <div className="ai-candidate-role">{expert.position}</div>
                            )}

                            <div className="ai-candidate-meta">
                              {expert.country && (
                                <span>
                                  <i className="fa-solid fa-location-dot mr-1" />
                                  {expert.country}
                                </span>
                              )}
                              {expert.languages && (
                                <span>
                                  <i className="fa-solid fa-language mr-1" />
                                  {String(expert.languages).slice(0, 45)}
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="align-middle">
                            <div className="d-flex flex-column align-items-start" style={{ gap: "4px" }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  fontSize: "0.82rem",
                                  fontWeight: 700,
                                  backgroundColor: scoreDetails.bg,
                                  color: scoreDetails.color,
                                  border: `1px solid ${scoreDetails.border}`,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {scoreDetails.text}
                              </span>

                              {totalMh > 0 ? (
                                <span
                                  className={`ai-mh-status ${
                                    matchedMh === totalMh
                                      ? "mh-full"
                                      : matchedMh > 0
                                      ? "mh-partial"
                                      : "mh-none"
                                  }`}
                                >
                                  {matchedMh === totalMh
                                    ? `✓ ${matchedMh}/${totalMh} Must-Haves`
                                    : `${matchedMh}/${totalMh} Must-Haves`}
                                </span>
                              ) : (
                                expert.musthave && (
                                  <span className="ai-mh-status mh-full">✓ Must-Have Match</span>
                                )
                              )}
                            </div>
                          </td>

                          <td className="align-middle">
                            <div className="ai-synthesis-text">
                              {expert.synthesis || "Relevant functional and domain experience."}
                            </div>
                            {(expert.experience || expert.skills) && (
                              <button
                                type="button"
                                className="btn btn-link btn-xs p-0 mt-1 ai-preview-toggle"
                                onClick={() =>
                                  setExpandedExpertId(isExpanded ? null : expert.id)
                                }
                              >
                                <i
                                  className={`fa-solid ${
                                    isExpanded ? "fa-chevron-up" : "fa-chevron-down"
                                  } mr-1`}
                                />
                                {isExpanded ? "Hide details" : "Preview experience & skills"}
                              </button>
                            )}
                          </td>

                          <td className="align-middle text-right">
                            {isAdded ? (
                              <button
                                className="btn unselect-profile-btn btn-sm"
                                onClick={() => handleUnselect(expert.id)}
                                disabled={loadingAction[expert.id]}
                              >
                                {loadingAction[expert.id] ? "..." : "✓ Selected"}
                              </button>
                            ) : (
                              <button
                                className="btn select-profile-btn btn-sm"
                                onClick={() => handleSelect(expert.id)}
                                disabled={loadingAction[expert.id]}
                              >
                                {loadingAction[expert.id] ? (
                                  "..."
                                ) : (
                                  <>
                                    <i className="fa-solid fa-plus mr-1" /> Select
                                  </>
                                )}
                              </button>
                            )}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="ai-expanded-row">
                            <td colSpan="5">
                              <div className="ai-expanded-box">
                                {expert.skills && (
                                  <div className="mb-2">
                                    <strong className="small text-uppercase text-muted d-block">
                                      Skills
                                    </strong>
                                    <div className="small">{expert.skills}</div>
                                  </div>
                                )}
                                {expert.experience && (
                                  <div>
                                    <strong className="small text-uppercase text-muted d-block">
                                      Experience
                                    </strong>
                                    <div
                                      className="small"
                                      style={{ whiteSpace: "pre-line", maxHeight: "140px", overflowY: "auto" }}
                                    >
                                      {expert.experience}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center mt-5 py-4">
                {hasSearchedEmpty ? (
                  <>
                    <p className="text-muted mb-2">
                      No candidates matched all strict criteria. Try removing a Must-Have chip or switching to Remote.
                    </p>
                    <button
                      type="button"
                      className="btn btn-sm base-btn"
                      onClick={() => executeSearch(summary, true, false)}
                    >
                      Retry Search
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-muted mb-2">Ready to search candidates for this project.</p>
                    <button
                      type="button"
                      className="btn base-btn"
                      onClick={() => executeSearch(summary, true, false)}
                      disabled={isBusy || !summary}
                    >
                      Search Experts Now
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar: Conversational AI Refinement */}
          {showChatPanel && results && results.length > 0 && (
            <div className="ai-chat-sidebar">
              <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                <span className="font-weight-bold small text-uppercase" style={{ color: "#365561" }}>
                  <i className="fa-solid fa-wand-magic-sparkles mr-1" /> AI Quick Refine
                </span>
                {chatHistory.length > 0 && (
                  <button
                    className="btn btn-xs btn-link text-danger p-0"
                    onClick={handleClearChat}
                    title="Reset list to all search results"
                  >
                    Reset ({results.length})
                  </button>
                )}
              </div>

              {/* Quick 1-click prompt pills */}
              {chatHistory.length === 0 && (
                <div className="mb-2">
                  <div className="text-muted" style={{ fontSize: "11px", marginBottom: "6px" }}>
                    Quick filters:
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
                    {[
                      "Only C-level / Directors",
                      "Must have consulting background",
                      "Top 10 strongest domain fit",
                    ].map((q) => (
                      <button
                        key={q}
                        type="button"
                        className="ai-quick-prompt-btn"
                        onClick={() => handleSendChatMessage(null, q)}
                        disabled={isChatLoading}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="chat-history-box flex-grow-1 mb-2" style={{ overflowY: "auto", padding: "4px" }}>
                {chatHistory.length === 0 && (
                  <div className="text-center mt-3 text-muted small">
                    <p className="mb-1">
                      Ask the AI to narrow down or re-rank these <b>{results.length}</b> candidates in plain language.
                    </p>
                  </div>
                )}
                {chatHistory.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`chat-message mb-2 p-2 rounded small ${
                      msg.role === "user"
                        ? "bg-primary text-white text-right ml-3"
                        : msg.role === "error"
                        ? "bg-danger text-white"
                        : "bg-light border mr-2"
                    }`}
                  >
                    {msg.content}
                  </div>
                ))}
                {isChatLoading && (
                  <div className="chat-message mb-2 p-2 rounded small bg-light border mr-2">
                    <img src={LoadingIcon} alt="thinking..." style={{ height: "14px" }} className="mr-1" />
                    Refining candidates...
                  </div>
                )}
              </div>

              <form onSubmit={(e) => handleSendChatMessage(e, null)} className="chat-input-area d-flex">
                <input
                  type="text"
                  className="form-control form-control-sm mr-1"
                  placeholder="e.g. Only German speakers..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled={isChatLoading}
                />
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={isChatLoading || !chatInput.trim()}
                >
                  <i className="fa-solid fa-paper-plane" />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
